import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength, isPasswordStrong } from "@/components/PasswordStrength";

export const SecurityPanel = () => {
  const { user, setUser } = useAuth();
  const [username, setUsername] = useState(user?.username || "");
  const [savingU, setSavingU] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [savingP, setSavingP] = useState(false);

  useEffect(() => {
    if (user?.username) setUsername(user.username);
  }, [user?.username]);

  const saveUsername = async (e) => {
    e.preventDefault();
    setSavingU(true);
    try {
      const { data } = await api.post("/auth/change-username", { username });
      setUser((u) => ({ ...u, username: data.username }));
      toast.success("Username updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingU(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(nw)) {
      toast.error("Password must be at least 8 characters with an uppercase letter, a lowercase letter, and a number.");
      return;
    }
    setSavingP(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur("");
      setNw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingP(false);
    }
  };

  return (
    <div className="card-soft" data-testid="security-panel">
      <h2 className="font-heading text-xl text-[#1E3F33] mb-4 flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-[#2D6A4F]" /> Login &amp; security
      </h2>
      <form onSubmit={saveUsername} className="space-y-2">
        <label className="label-soft">Username</label>
        <input data-testid="security-username-input" className="input-soft" value={username} onChange={(e) => setUsername(e.target.value)} minLength={3} maxLength={30} autoCapitalize="none" required />
        <button data-testid="security-username-save" disabled={savingU} className="btn-secondary text-xs py-2">{savingU ? "Saving…" : "Update username"}</button>
      </form>
      <form onSubmit={savePassword} className="space-y-2 mt-5 pt-5 border-t border-[#1E3F33]/8">
        <label className="label-soft">Current password</label>
        <PasswordInput testId="security-current-password" toggleTestId="security-current-password-toggle" required value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" />
        <label className="label-soft">New password</label>
        <PasswordInput testId="security-new-password" toggleTestId="security-new-password-toggle" required minLength={8} value={nw} onChange={(e) => setNw(e.target.value)} autoComplete="new-password" />
        <PasswordStrength password={nw} testId="security-password-strength" />
        <button data-testid="security-password-save" disabled={savingP} className="btn-secondary text-xs py-2">{savingP ? "Saving…" : "Update password"}</button>
      </form>
    </div>
  );
};
